import { useCallback, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import {
  generateGoogleCalendarLink,
  generateCalendarICS,
  generateCalendarFilename,
  downloadCalendarFile,
  normalizeEventData,
  type CalendarEventData,
} from "@/utils/calendarUtils";

/**
 * Hook for adding events to Google Calendar or Apple Calendar.
 * On iOS, Apple Calendar uses native EKEventEditViewController.
 * On web/Android, falls back to .ics download.
 */
export function useAddToCalendar(entity: any, entityId?: string) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const calendarData = useMemo<CalendarEventData | null>(() => {
    if (!entity || !entityId) return null;
    return { sourceEntity: entity, entityId };
  }, [entity, entityId]);

  const handleGoogleCalendar = useCallback(() => {
    if (!calendarData) return;
    const link = generateGoogleCalendarLink(calendarData);
    if (link) window.open(link, "_blank", "noopener,noreferrer");
    setDrawerOpen(false);
  }, [calendarData]);

  const handleAppleCalendar = useCallback(async () => {
    if (!calendarData) return;
    setDrawerOpen(false);

    if (Capacitor.isNativePlatform()) {
      try {
        const { CapacitorCalendar } = await import("@ebarooni/capacitor-calendar");
        const normalized = normalizeEventData(calendarData);
        if (!normalized.dtstart) return;

        // For all-day events, extract date components directly and create local midnight
        // to avoid UTC→local timezone shift (UTC midnight May 9 → May 8 8PM in US timezones)
        const toLocalMidnight = (dateStr: string): number => {
          const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
          return new Date(dateStr).getTime();
        };

        const startDate = normalized.allDay
          ? toLocalMidnight(normalized.dtstart)
          : new Date(normalized.dtstart).getTime();
        const endDate = normalized.dtend
          ? normalized.allDay ? toLocalMidnight(normalized.dtend) : new Date(normalized.dtend).getTime()
          : startDate + 2 * 60 * 60 * 1000;

        await CapacitorCalendar.createEventWithPrompt({
          title: normalized.title,
          location: normalized.location || undefined,
          startDate,
          endDate,
          isAllDay: normalized.allDay,
          url: normalized.url || undefined,
          notes: normalized.description || undefined,
        });
        return;
      } catch (error: any) {
        if (error?.message?.includes("cancel")) return;
        console.error("Native calendar failed, falling back to .ics:", error);
      }
    }

    const ics = generateCalendarICS(calendarData);
    if (!ics) return;
    const title = entity?.name || entity?.entity_name || "event";
    await downloadCalendarFile(ics, generateCalendarFilename(title));
  }, [calendarData, entity]);

  return {
    drawerOpen,
    setDrawerOpen,
    handleGoogleCalendar,
    handleAppleCalendar,
  };
}
